// @owners { team: patients }
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { setShipments } from '~shared/actions/shipments';
import getIsAuthorized from '~shared/features/auth/selectors/getIsAuthorized';
import { staleTimes } from '~shared/queries/constants';
import { queries } from '~shared/queries/query-keys';
import { useDispatchShared, useSelectorShared } from '~shared/store';

/**
 * Fetch the patient's shipments — the React Query replacement for the deprecated
 * fetchShipments thunk. Runs only when authorized.
 */
export const useGetShipments = () => {
  const isAuthorized = useSelectorShared(getIsAuthorized);
  const { data, isLoading, isSuccess } = useQuery({
    ...queries.shipments.fetchAll,
    enabled: isAuthorized,
    staleTime: staleTimes.fifteenMinutes,
    gcTime: staleTimes.fifteenMinutes,
  });

  return { shipments: data ?? [], isLoading, isSuccess };
};

/**
 * Bridge: mirror the React Query shipments data into the Redux `shipments.shipments`
 * slice so existing Reselect selectors (getShipments → checkout's getOrder, etc.) keep
 * working without a Redux networking action. One-directional (RQ → Redux); the slice is
 * written only by the fetch, so an errored refetch never clobbers it. Mount once at an
 * always-rendered authed root. Transitional bridge toward strategy A — see the
 * redux-thunk-to-react-query-migration skill.
 */
export const useShipmentsReduxBridge = () => {
  const dispatch = useDispatchShared();
  const { shipments, isSuccess } = useGetShipments();

  useEffect(() => {
    if (isSuccess) {
      dispatch(setShipments(shipments));
    }
  }, [dispatch, isSuccess, shipments]);
};
